import type { FilterRuleModel } from '@/shared/components/filter/types'

import { useMemo } from 'react'
import { any } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import {
	createChoices,
	toAdaptedSchema,
	useColumnsSchemaQuery,
} from '@/entities/column'
import { useBrandsQuery, useCategoriesQuery } from '@/entities/core'

import {
	getQueryDescription,
	Operators,
} from '@/shared/components/query-builder'
import { LinkButton } from '@/shared/components/ui/link-button'
import { HiddenOnContext } from '@/shared/generated'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { useModals } from '@/shared/lib/modal-manager'

const Locks = ({
	rules,
	params,
}: {
	params?: { optimization_group_id: string }
	rules: (
		FilterRuleModel | { name: null; operation: string; value: number | boolean }
	)[]
}) => {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const isForceLocked = any({ name: 'sku', operation: Operators.IS }, rules)
	const defaultRules = rules?.filter((item) => !item.name) as {
		name: null
		operation: string
		value: number | boolean
	}[]
	const customRules = rules?.filter((item) => !!item.name) as FilterRuleModel[]

	return isForceLocked ? (
		t('price_notif_is_locked_desc')
	) : (
		<p>
			{t('price_notif_is_locked_desc_by_stop_list')}{' '}
			{params?.optimization_group_id ? (
				<LinkButton
					variant='brand'
					onClick={() =>
						showModal('OPTIMIZATION_GROUP_MODAL', {
							optimizationGroupId: params.optimization_group_id,
							initialTabId: 'stop_list',
						})
					}
				>
					{t('stop_list')}
				</LinkButton>
			) : (
				t('stop_list')
			)}
			{defaultRules.length > 0 && <StopListDefaultRules rules={defaultRules} />}
			{customRules.length > 0 && <StopListCustomRules rules={customRules} />}
		</p>
	)
}

const StopListDefaultRules = ({
	rules,
}: {
	rules: { name: null; operation: string; value: number | boolean }[]
}) => {
	const { t } = useTranslation()

	return (
		<ul className='mb-0 list-disc'>
			{rules.map((item, index) => (
				<li key={`${item.operation || ''} ${item?.value || ''}`}>
					{`${t(`og_stoplist_default_rules_${item.operation}`, { defaultValue: item.operation || '' })} ${item.value || ''}`}
				</li>
			))}
		</ul>
	)
}

const StopListCustomRules = ({ rules }: { rules: FilterRuleModel[] }) => {
	const { t } = useTranslation()

	const optimizationGroupId = useOptimizationGroupId()!

	const { data: schema } = useColumnsSchemaQuery(HiddenOnContext.Assortment)
	const { data: brand } = useBrandsQuery(
		optimizationGroupId ? { optimization_group_id: optimizationGroupId } : {}
	)
	const { data: category } = useCategoriesQuery(
		optimizationGroupId ? { optimization_group_id: optimizationGroupId } : {}
	)

	const dataChoices = useMemo(
		() =>
			createChoices({
				schema: schema || [],
				entities: {
					brand: brand || [],
					category: category || [],
				},
			}),
		[schema, brand, category]
	)
	const humanizedRules = getQueryDescription(
		(rules as FilterRuleModel[]) || [],
		{
			fields: (schema || []).map((schema) => toAdaptedSchema(schema, t)),
			dataChoices,
		}
	)

	return (
		<ul className='mb-0 list-disc'>
			{humanizedRules
				?.filter((item) => !!item.label)
				.map((item, index) => (
					<li key={index}>
						{`${item.label} ${item?.operation || ''} ${item?.value || ''}`}
					</li>
				))}
		</ul>
	)
}

export default Locks
